import React, { useState } from 'react';
import { User, UserRole } from '../../types';
import { Card, Button } from '../UI';
import { useTitle } from '../../hooks';
import { usersApi } from '../../services/api.service';
import { 
  Lock, 
  Eye, 
  EyeOff, 
  CheckCircle, 
  AlertCircle,
  Shield,
  User as UserIcon,
  Mail,
  Briefcase
} from 'lucide-react';

interface SettingsProps {
  currentUser: User;
}

export const Settings: React.FC<SettingsProps> = ({ currentUser }) => {
  useTitle('Pengaturan');

  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Password validation
  const passwordErrors: string[] = [];
  if (newPassword && newPassword.length < 6) {
    passwordErrors.push('Minimal 6 karakter');
  }
  if (confirmPassword && newPassword !== confirmPassword) {
    passwordErrors.push('Password tidak cocok');
  }

  const isFormValid = 
    currentPassword.length > 0 && 
    newPassword.length >= 6 && 
    newPassword === confirmPassword;

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isFormValid) return;
    
    setIsLoading(true);
    setMessage(null);
    
    try {
      await usersApi.changeMyPassword(currentPassword, newPassword);
      setMessage({ type: 'success', text: 'Password berhasil diubah!' });
      // Reset form
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || error?.message || 'Gagal mengubah password';
      setMessage({ type: 'error', text: errorMessage });
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleLabel = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMINISTRATOR:
        return 'Administrator';
      case UserRole.STAF_ADMIN:
        return 'Staf Admin';
      case UserRole.STAF_HO:
        return 'Staf HO';
      case UserRole.STAF_FINANCE:
        return 'Staf Finance';
      default:
        return role;
    }
  };

  const getRoleBadgeColor = (role: UserRole) => {
    switch (role) {
      case UserRole.ADMINISTRATOR:
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case UserRole.STAF_ADMIN:
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case UserRole.STAF_HO:
        return 'bg-green-100 text-green-700 border-green-200';
      case UserRole.STAF_FINANCE:
        return 'bg-orange-100 text-orange-700 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
          <Shield className="w-5 h-5 text-indigo-600" />
        </div>
        <div>
          <h1 className="text-xl lg:text-2xl font-bold text-gray-900">Pengaturan</h1>
          <p className="text-sm text-gray-500">Kelola akun dan keamanan Anda</p>
        </div>
      </div>

      {/* Profile Info Card */}
      <Card className="p-4 lg:p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <UserIcon className="w-5 h-5 text-gray-400" />
          Informasi Profil
        </h2>
        
        <div className="space-y-4">
          {/* Name */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center shrink-0">
              <span className="text-indigo-600 font-bold">
                {currentUser.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-500">Nama</p>
              <p className="font-medium text-gray-900 truncate">{currentUser.name}</p>
            </div>
          </div>

          {/* Email */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center shrink-0">
              <Mail className="w-5 h-5 text-blue-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-500">Email</p>
              <p className="font-medium text-gray-900 truncate">{currentUser.email}</p>
            </div>
          </div>

          {/* Role */}
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <Briefcase className="w-5 h-5 text-green-600" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs text-gray-500">Peran</p>
              <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium border ${getRoleBadgeColor(currentUser.role)}`}>
                {getRoleLabel(currentUser.role)}
              </span>
            </div>
          </div>
        </div>
      </Card>

      {/* Change Password Card */}
      <Card className="p-4 lg:p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <Lock className="w-5 h-5 text-gray-400" />
          Ubah Password
        </h2>

        {/* Message Alert */}
        {message && (
          <div className={`mb-4 p-4 rounded-xl flex items-start gap-3 ${
            message.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-200' 
              : 'bg-red-50 text-red-800 border border-red-200'
          }`}>
            {message.type === 'success' ? (
              <CheckCircle className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
            )}
            <p className="text-sm">{message.text}</p>
          </div>
        )}

        <form onSubmit={handleChangePassword} className="space-y-4">
          {/* Current Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Password Saat Ini
            </label>
            <div className="relative">
              <input
                type={showCurrentPassword ? 'text' : 'password'}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Masukkan password saat ini"
                className="block w-full rounded-xl border border-gray-300 bg-white text-gray-900 shadow-sm 
                  focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 focus:outline-none
                  placeholder:text-gray-400 h-12 sm:h-11 text-base sm:text-sm transition-colors duration-200
                  px-3 pr-10 hover:border-gray-400"
                required
              />
              <button
                type="button"
                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-target"
              >
                {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* New Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Password Baru
            </label>
            <div className="relative">
              <input
                type={showNewPassword ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Masukkan password baru (min. 6 karakter)"
                className={`block w-full rounded-xl border bg-white text-gray-900 shadow-sm 
                  focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 focus:outline-none
                  placeholder:text-gray-400 h-12 sm:h-11 text-base sm:text-sm transition-colors duration-200
                  px-3 pr-10 ${newPassword && newPassword.length < 6 ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 hover:border-gray-400'}`}
                required
                minLength={6}
              />
              <button
                type="button"
                onClick={() => setShowNewPassword(!showNewPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-target"
              >
                {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {newPassword && newPassword.length < 6 && (
              <p className="text-xs text-red-500 mt-1">Password minimal 6 karakter</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Konfirmasi Password Baru
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Ulangi password baru"
                className={`block w-full rounded-xl border bg-white text-gray-900 shadow-sm 
                  focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 focus:outline-none
                  placeholder:text-gray-400 h-12 sm:h-11 text-base sm:text-sm transition-colors duration-200
                  px-3 pr-10 ${confirmPassword && newPassword !== confirmPassword ? 'border-red-300 focus:ring-red-500' : 'border-gray-300 hover:border-gray-400'}`}
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 touch-target"
              >
                {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {confirmPassword && newPassword !== confirmPassword && (
              <p className="text-xs text-red-500 mt-1">Password tidak cocok</p>
            )}
          </div>

          {/* Submit Button */}
          <div className="pt-2">
            <Button
              type="submit"
              disabled={!isFormValid || isLoading}
              className="w-full sm:w-auto"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Menyimpan...
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4 mr-2" />
                  Ubah Password
                </>
              )}
            </Button>
          </div>
        </form>

        {/* Security Tips */}
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <h3 className="text-sm font-medium text-amber-800 mb-2">Tips Keamanan:</h3>
          <ul className="text-xs text-amber-700 space-y-1.5">
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">•</span>
              Gunakan kombinasi huruf besar, huruf kecil, angka, dan simbol
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">•</span>
              Jangan gunakan password yang sama dengan akun lain
            </li>
            <li className="flex items-start gap-2">
              <span className="text-amber-500 mt-0.5">•</span>
              Ubah password secara berkala untuk keamanan optimal
            </li>
          </ul>
        </div>
      </Card>
    </div>
  );
};

export default Settings;
